import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Alert, Card, Container, Form, Table } from 'react-bootstrap'
import { fetchAuditLog, type AuditLogEntryDto } from '../services/api'

export default function AuditLog() {
  const [entityType, setEntityType] = useState('')
  const [entityId, setEntityId] = useState('')
  const [rangeDays, setRangeDays] = useState(30)

  const { data: entries = [], isLoading, isError } = useQuery({
    queryKey: ['audit-log-page', entityType, entityId, rangeDays],
    queryFn: async () => {
      const now = new Date()
      const from = new Date(now.getTime() - rangeDays * 24 * 60 * 60 * 1000)
      return fetchAuditLog({
        entityType: entityType.trim() || undefined,
        entityId: entityId.trim() ? entityId.trim() : undefined,
        fromUtc: from.toISOString(),
        toUtc: now.toISOString(),
        take: 500,
      })
    },
  })

  return (
    <Container className="py-3">
      <h1 className="mb-3">Audit events</h1>
      <p className="text-muted mb-3">
        System-level audit log of key actions, including check runs, approvals, findings, and user activity.
      </p>

      <Card className="mb-3">
        <Card.Body>
          <Form className="row g-2 align-items-end">
            <Form.Group className="col-md-3">
              <Form.Label className="small">Entity type (optional)</Form.Label>
              <Form.Control
                size="sm"
                value={entityType}
                onChange={(e) => setEntityType(e.target.value)}
                placeholder="e.g. CheckRun, Finding"
              />
            </Form.Group>
            <Form.Group className="col-md-4">
              <Form.Label className="small">Entity ID (optional)</Form.Label>
              <Form.Control
                size="sm"
                value={entityId}
                onChange={(e) => setEntityId(e.target.value)}
                placeholder="GUID"
              />
            </Form.Group>
            <Form.Group className="col-md-2">
              <Form.Label className="small">Last (days)</Form.Label>
              <Form.Control
                size="sm"
                type="number"
                min={1}
                max={365}
                value={rangeDays}
                onChange={(e) => setRangeDays(Number(e.target.value) || 30)}
              />
            </Form.Group>
          </Form>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          {isLoading && <div className="text-muted">Loading audit events…</div>}
          {isError && (
            <Alert variant="danger" className="mb-0">
              Failed to load audit log.
            </Alert>
          )}
          {!isLoading && !isError && entries.length === 0 && (
            <div className="text-muted">No audit events for the selected filters.</div>
          )}
          {!isLoading && !isError && entries.length > 0 && (
            <Table size="sm" striped hover responsive>
              <thead>
                <tr>
                  <th>Time (UTC)</th>
                  <th>Actor</th>
                  <th>Action</th>
                  <th>Entity</th>
                  <th>Description</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((a: AuditLogEntryDto) => (
                  <tr key={a.id}>
                    <td>{new Date(a.actionUtc).toISOString()}</td>
                    <td>{a.actor}</td>
                    <td>{a.actionType}</td>
                    <td>
                      {a.entityType} ({a.entityId})
                    </td>
                    <td>{a.description}</td>
                    <td>{a.result}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </Container>
  )
}

